import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { CheckCircleOutline, Close } from '@mui/icons-material';

import { PackagingChoices, GROUP_BY_LABELS, estimateBytes, formatBytes } from './packaging';

export interface PacketSummaryPatientRow {
  id: string;
  label: string;
  subLabel?: string;
  count: number;
}

interface Props {
  patientLabel: string;
  // When provided (the by-cohort flow), renders the "PATIENTS INCLUDED" roster below the
  // checklist instead of the single-patient box — by-patient never passes this, since there's
  // only ever the one patient to show.
  patients?: PacketSummaryPatientRow[];
  periodLabel: string;
  documentCount: number;
  choices: PackagingChoices;
  fileName: string;
  onFileNameChange: (value: string) => void;
  adrReference: string;
  onAdrReferenceChange: (value: string) => void;
  onReview: () => void;
}

const VISIBLE_PATIENTS = 4;
const MODAL_PAGE_SIZE = 8;

/**
 * SQ-168, step 4 — the packet, described rather than configured.
 *
 * No toggles live here. The arrangement controls are one click away in Review & request,
 * which keeps this a sentence someone can read and check instead of a second form to
 * fill in (E1, E2).
 */
export const PacketSummary: React.FC<Props> = ({
  patientLabel,
  patients,
  periodLabel,
  documentCount,
  choices,
  fileName,
  onFileNameChange,
  adrReference,
  onAdrReferenceChange,
  onReview,
}) => {
  const [rosterOpen, setRosterOpen] = React.useState(false);
  const [page, setPage] = React.useState(0);
  const hasRoster = !!patients && patients.length > 0;
  const visiblePatients = hasRoster ? patients!.slice(0, VISIBLE_PATIENTS) : [];
  const totalPages = hasRoster ? Math.ceil(patients!.length / MODAL_PAGE_SIZE) : 1;
  const modalPatients = hasRoster
    ? patients!.slice(page * MODAL_PAGE_SIZE, (page + 1) * MODAL_PAGE_SIZE)
    : [];
  const openRoster = () => {
    setPage(0);
    setRosterOpen(true);
  };
  const patientCountLabel = hasRoster
    ? `${patients!.length} ${patients!.length === 1 ? 'patient' : 'patients'}`
    : '1 patient';

  const lines = [
    choices.deliverAs === 'single_pdf'
      ? `One PDF, sections by ${GROUP_BY_LABELS[
          choices.groupBy
        ].toLowerCase()}, oldest document first`
      : `One folder per ${GROUP_BY_LABELS[choices.groupBy].toLowerCase()}, oldest document first`,
    choices.documentHandling === 'merge' && choices.deliverAs === 'zip'
      ? 'Each folder merged into a single PDF'
      : 'Daily notes combined into a single PDF; everything else kept as its own file',
  ];

  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 700, letterSpacing: '0.04em' }}
      >
        WHAT YOU&apos;LL GET
      </Typography>

      <Stack
        direction="row"
        alignItems="baseline"
        spacing={0.75}
        sx={{ mt: 0.5 }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: 700 }}
        >
          {documentCount}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
        >
          {documentCount === 1 ? 'document' : 'documents'}
        </Typography>
      </Stack>
      <Typography
        variant="caption"
        color="text.secondary"
      >
        across {patientCountLabel}
        {periodLabel && ` · ${periodLabel}`}
        {/* "about" is the honest word: nothing has been built yet, so this is an
            estimate, not a measurement (E1). */}
        {' · about '}
        {formatBytes(estimateBytes(documentCount))}
      </Typography>

      <Divider sx={{ my: 2 }} />

      <Stack spacing={1}>
        {lines.map((line) => (
          <Stack
            key={line}
            direction="row"
            spacing={1}
            alignItems="center"
          >
            <CheckCircleOutline sx={{ fontSize: 16, color: 'success.main' }} />
            <Typography
              variant="body2"
              color="text.secondary"
            >
              {line}
            </Typography>
          </Stack>
        ))}
      </Stack>

      <Divider sx={{ my: 2 }} />

      {hasRoster ? (
        <>
          <Stack
            direction="row"
            alignItems="baseline"
            justifyContent="space-between"
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 700, letterSpacing: '0.04em' }}
            >
              PATIENTS INCLUDED
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
            >
              {patients!.length}
            </Typography>
          </Stack>
          <Stack
            spacing={1}
            sx={{ mt: 1 }}
          >
            {visiblePatients.map((p) => (
              <Stack
                key={p.id}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  px: 1.5,
                  py: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Box>
                  <Typography variant="body2">{p.label}</Typography>
                  {p.subLabel && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      {p.subLabel}
                    </Typography>
                  )}
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  {p.count}
                </Typography>
              </Stack>
            ))}
          </Stack>
          {patients!.length > VISIBLE_PATIENTS && (
            <Typography
              variant="body2"
              color="primary"
              onClick={openRoster}
              sx={{ mt: 1, textAlign: 'center', cursor: 'pointer' }}
            >
              {`View all ${patients!.length} patients`}
            </Typography>
          )}
          <Dialog
            open={rosterOpen}
            onClose={() => setRosterOpen(false)}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle sx={{ pr: 7 }}>
              Patients included
              <Typography
                variant="body2"
                color="text.secondary"
              >
                {patients!.length} patients in this download
              </Typography>
              <IconButton
                aria-label="Close"
                onClick={() => setRosterOpen(false)}
                sx={{ position: 'absolute', right: 12, top: 12 }}
              >
                <Close fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              {modalPatients.map((p) => (
                <Stack
                  key={p.id}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}
                >
                  <Typography variant="body2">
                    {p.label}
                    {p.subLabel && (
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 1 }}
                      >
                        {p.subLabel}
                      </Typography>
                    )}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    {p.count} {p.count === 1 ? 'doc' : 'docs'}
                  </Typography>
                </Stack>
              ))}
            </DialogContent>
            <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', p: 2, gap: 1 }}>
              <Stack
                direction="row"
                alignItems="center"
                // justifyContent="center"
                spacing={2}
              >
                <Button
                  size="small"
                  disabled={page === 0}
                  onClick={() => setPage((x) => x - 1)}
                >
                  ← Previous
                </Button>
                <Typography variant="caption">
                  Page {page + 1} of {totalPages}
                </Typography>
                <Button
                  size="small"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((x) => x + 1)}
                >
                  Next →
                </Button>
              </Stack>
              <Button
                fullWidth
                onClick={() => setRosterOpen(false)}
                sx={{ color: '#6C737F' }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ) : (
        <>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 700, letterSpacing: '0.04em' }}
          >
            PATIENT
          </Typography>
          <Box
            sx={{
              mt: 1,
              px: 1.5,
              py: 1,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Typography variant="body2">{patientLabel}</Typography>
          </Box>
        </>
      )}

      <Stack
        spacing={2}
        sx={{ mt: 3 }}
      >
        <TextField
          fullWidth
          size="small"
          label="File name"
          value={fileName}
          onChange={(e) => onFileNameChange(e.target.value)}
        />
        <TextField
          fullWidth
          size="small"
          label="ADR / reference — optional"
          value={adrReference}
          placeholder='e.g. ADR-2026-118'
          onChange={(e) => onAdrReferenceChange(e.target.value)}
        />
      </Stack>

      <Button
        fullWidth
        variant="contained"
        sx={{ mt: 2 }}
        disabled={documentCount === 0}
        onClick={onReview}
      >
        Request download
      </Button>
    </Box>
  );
};